// components/admin/MultilingualOptionEditor/MultilingualOptionEditor.js
import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import MultilingualInput from '../MultilingualInput/MultilingualInput';
import './MultilingualOptionEditor.css';

const MultilingualOptionEditor = ({
    option,
    onOptionUpdate,
    onOptionRemove,
    onValueAdd,
    onValueRemove
}) => {
    const { availableLanguages } = useLanguage();
    const [newValueInput, setNewValueInput] = useState({});
    const [showValueForm, setShowValueForm] = useState(false);

    const handleOptionNameChange = (multilingualName) => {
        onOptionUpdate({
            ...option,
            name: multilingualName
        });
    };

    const handleAddValue = () => {
        // Check if we have at least one language filled
        const hasValue = Object.values(newValueInput).some(val => val && val.trim());
        if (!hasValue) return;

        // Create simple multilingual value
        const valueToAdd = JSON.stringify(newValueInput);

        onValueAdd(option.id, valueToAdd);
        setNewValueInput({});
        setShowValueForm(false);
    };

    const parseValue = (value) => {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    };

    const getDisplayValue = (value) => {
        const parsed = parseValue(value);

        if (typeof parsed === 'object' && parsed !== null) {
            // Show English or first available language
            const displayText = parsed.en || Object.values(parsed)[0] || 'Unnamed';
            return displayText;
        }

        return parsed;
    };

    const handleValueInputChange = (language, value) => {
        setNewValueInput(prev => ({
            ...prev,
            [language]: value
        }));
    };

    return (
        <div className="multilingual-option-editor">
            <div className="option-header">
                <MultilingualInput
                    label="Option Name"
                    value={option.name}
                    onChange={handleOptionNameChange}
                    placeholder="e.g., Color, Size, Material"
                />
                <button
                    type="button"
                    onClick={() => onOptionRemove(option.id)}
                    className="remove-option-btn"
                >
                    Remove Option
                </button>
            </div>

            <div className="option-values">
                <h4>Values</h4>

                {option.values && option.values.map((value, index) => (
                    <div key={index} className="option-value-item">
                        <span className="value-display">
                            {getDisplayValue(value)}
                        </span>
                        <button
                            type="button"
                            onClick={() => onValueRemove(option.id, value)}
                            className="remove-value-btn"
                        >
                            ×
                        </button>
                    </div>
                ))}

                {!showValueForm ? (
                    <button
                        type="button"
                        onClick={() => setShowValueForm(true)}
                        className="add-value-btn"
                    >
                        + Add Value
                    </button>
                ) : (
                    <div className="add-value-form">
                        <div className="multilingual-value-inputs">
                            {availableLanguages.map(lang => (
                                <div key={lang.code} className="value-input-group">
                                    <label className="value-input-label">
                                        {lang.name}:
                                    </label>
                                    <input
                                        type="text"
                                        value={newValueInput[lang.code] || ''}
                                        onChange={(e) => handleValueInputChange(lang.code, e.target.value)}
                                        placeholder={`Value in ${lang.name}`}
                                        className="value-input"
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="value-form-actions">
                            <button
                                type="button"
                                onClick={handleAddValue}
                                className="confirm-value-btn"
                            >
                                Add
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowValueForm(false);
                                    setNewValueInput({});
                                }}
                                className="cancel-value-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultilingualOptionEditor;
